import { useCallback, useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

// Image + alt text stay together in one slot group so a swapped image doesn't
// leave a stale alt description behind. No defaultField / sourceBinding — the
// community site-map illustration isn't stored in the property model or any
// gallery collection, so operators upload it manually per property.
const imageDef: SlotGroupDefinition = {
  label: 'Site Map Image',
  description: 'Upload a static image of the property site map.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community site map' },
  },
};

// ============================================================================
// Lightbox (single image)
// ============================================================================

function SiteMapLightbox({
  url,
  alt,
  onClose,
}: {
  url: string;
  alt: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      className="sitemap-lightbox"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Site map lightbox"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="sitemap-lightbox__close"
        aria-label="Close lightbox"
      >
        &times;
      </button>

      <div className="sitemap-lightbox__img-wrap" onClick={(e) => e.stopPropagation()}>
        <VibesitesImage
          url={url}
          alt={alt}
          sizes="100vw"
          className="max-w-full max-h-[90vh] object-contain sitemap-lightbox__img"
        />
      </div>

      <style>{`
        .sitemap-lightbox {
          position: fixed;
          inset: 0;
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.85);
          padding: 1rem;
        }

        .sitemap-lightbox__img-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          max-width: calc(100vw - 2rem);
          max-height: 90vh;
        }

        .sitemap-lightbox__close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          color: #fff;
          font-size: 2.25rem;
          line-height: 1;
          background: none;
          border: none;
          cursor: pointer;
          opacity: 0.85;
          z-index: 10;
          padding: 0.25rem 0.5rem;
          transition: opacity 0.15s;
        }
        .sitemap-lightbox__close:hover { opacity: 1; }

        .sitemap-lightbox__img {
          pointer-events: none;
          -webkit-user-drag: none;
          user-select: none;
        }

        @media (max-width: 639px) {
          .sitemap-lightbox__img-wrap {
            max-width: 100vw;
          }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

/**
 * Site Map page image: a large, centered static image of the property's
 * community site map. Sits below `SiteMapIntro` in the same slot the
 * `InteractiveMapSection` occupies on the interactive-map page — but without a
 * live map integration, so operators drop in a hand-authored site plan graphic.
 * An optional caption below the image supports labels / legends / attributions.
 * Clicking the image opens a full-screen lightbox for closer inspection.
 */
export function SiteMapImage() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const image = useSlotGroup('sitemapimage.image', imageDef);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const openLightbox = useCallback(() => setLightboxOpen(true), []);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  const alt = image.alt || 'Community site map';

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pb-16 md:pb-24"
    >
      <div className="mx-auto max-w-6xl px-3 sm:px-4 lg:px-6">
        <EditableSlotGroup groupPath="sitemapimage.image" definition={imageDef}>
          {image.image ? (
            <button
              type="button"
              onClick={openLightbox}
              className="block w-full cursor-zoom-in border-0 bg-transparent p-0 text-left"
              aria-label={`View larger: ${alt}`}
            >
              <VibesitesImage
                url={image.image}
                alt={alt}
                sizes="(min-width: 1024px) 1024px, 100vw"
                className="w-full h-auto object-contain rounded-lg shadow-md bg-[var(--vibe-color-surface)]"
              />
            </button>
          ) : (
            // Placeholder in edit mode — still clickable via the wrapper so an
            // operator can open the image picker even before an image is set.
            <div className="flex min-h-[300px] md:min-h-[400px] w-full items-center justify-center rounded-lg border-2 border-dashed border-[var(--vibe-color-text-muted)] bg-[var(--vibe-color-surface)] p-8 text-center">
              <p className="font-body text-sm text-[var(--vibe-color-text-muted)]">
                Click to upload a site map image.
              </p>
            </div>
          )}
        </EditableSlotGroup>

        <RichTextContent
          path="sitemapimage.caption"
          label="Caption"
          as="div"
          className="mt-4 font-body text-center text-sm italic text-[var(--vibe-color-text-muted)] [&_p]:mb-2 [&_a]:text-primary [&_a]:underline"
        />
      </div>

      {lightboxOpen && image.image ? (
        <SiteMapLightbox url={image.image} alt={alt} onClose={closeLightbox} />
      ) : null}
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'SiteMapImage',
  displayName: 'Site Map Image',
  tokens: {
    'sitemapimage.caption': {
      label: 'Caption',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'sitemapimage.image': imageDef,
  },
};

(SiteMapImage as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SiteMapImage;
